import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Grid, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { practice } from '@/api/endpoints';
import { api } from '@/api/client';
import { formatPrice } from '@/utils/format';
import type { MedicalProcedure, MedicalSpecialty, Modality } from '@/types/medical';

const MODALITIES: { code: Modality; label: string }[] = [
  { code: 'in_person', label: 'In person' },
  { code: 'video',     label: 'Video' },
  { code: 'phone',     label: 'Phone' },
  { code: 'async',     label: 'Async chat' },
  { code: 'home',      label: 'Home visit' },
];

const CURRENCIES = ['EUR', 'USD', 'PLN', 'GBP', 'CHF', 'SEK', 'NOK', 'DKK'];

export function PracticeProceduresPage() {
  const q = useApiQuery({
    queryKey: ['practice', 'procedures'],
    queryFn:  practice.procedures.list as () => Promise<MedicalProcedure[]>,
  });

  const [editing, setEditing] = useState<MedicalProcedure | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHeader
        title="Procedures"
        subtitle="What patients can book at your practice."
        actions={
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreating(true)}>
            New procedure
          </Button>
        }
      />

      <DataState
        query={q}
        empty={{
          title: 'No procedures yet',
          description: 'Add the first procedure patients can book — set duration, price, modality.',
        }}
      >
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((p) => (
              <ProcedureRow key={p.id} procedure={p} onEdit={() => setEditing(p)} />
            ))}
          </Stack>
        )}
      </DataState>

      {(creating || editing) && (
        <ProcedureDialog
          procedure={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
        />
      )}
    </>
  );
}

function ProcedureRow({
  procedure, onEdit,
}: { procedure: MedicalProcedure; onEdit: () => void }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <MedicalServicesIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {procedure.name}
              </Typography>
              {!procedure.is_active && (
                <Chip size="small" label="inactive" variant="outlined" />
              )}
              {procedure.requires_intake_form && (
                <Chip size="small" label="intake required" variant="outlined" color="primary" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {procedure.specialty?.name || '—'}
              {' · '}<span className="tabular">{procedure.duration_minutes} min</span>
              {' · '}{(procedure.modalities ?? []).join(', ')}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="subtitle1" className="tabular" fontWeight={600}>
              {procedure.price_amount
                ? formatPrice(procedure.price_amount, procedure.price_currency)
                : '—'}
            </Typography>
          </Box>
          <Button size="small" variant="text" startIcon={<EditIcon />} onClick={onEdit}>
            Edit
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ProcedureDialog({
  procedure, onClose,
}: { procedure: MedicalProcedure | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = procedure !== null;

  const specialtiesQ = useApiQuery({
    queryKey: ['specialties'],
    queryFn:  () => api.get<MedicalSpecialty[]>('/api/medical/specialties/'),
  });
  const intakeTemplatesQ = useApiQuery({
    queryKey: ['practice', 'intake-templates'],
    queryFn:  practice.intakeTemplates.list as () => Promise<Array<{ id: number; name: string }>>,
  });

  const [name, setName] = useState(procedure?.name ?? '');
  const [shortDesc, setShortDesc] = useState(procedure?.short_description ?? '');
  const [description, setDescription] = useState(procedure?.description ?? '');
  const [specialtyId, setSpecialtyId] = useState<string>(
    procedure?.specialty?.id ? String(procedure.specialty.id) : '');
  const [duration, setDuration] = useState<string>(
    String(procedure?.duration_minutes ?? 30));
  const [buffer, setBuffer] = useState<string>(String(procedure?.buffer_minutes ?? 0));
  const [modalities, setModalities] = useState<Modality[]>(procedure?.modalities ?? ['in_person']);
  const [price, setPrice] = useState<string>(procedure?.price_amount ?? '');
  const [currency, setCurrency] = useState(procedure?.price_currency ?? 'EUR');
  const [requiresIntake, setRequiresIntake] = useState(procedure?.requires_intake_form ?? false);
  const [intakeTemplateId, setIntakeTemplateId] = useState<string>(
    procedure?.intake_form_template ? String(procedure.intake_form_template) : '');
  const [requiresInPerson, setRequiresInPerson] = useState(
    procedure?.requires_first_visit_in_person ?? false);
  const [isActive, setIsActive] = useState(procedure?.is_active ?? true);

  useEffect(() => {
    // Re-sync if procedure prop changes (rare — dialog opens fresh)
  }, [procedure?.id]);

  const saveMut = useApiMutation({
    mutationFn: () => {
      const payload = {
        name, short_description: shortDesc, description,
        specialty_id: specialtyId ? parseInt(specialtyId, 10) : null,
        duration_minutes: parseInt(duration, 10) || 30,
        buffer_minutes: parseInt(buffer, 10) || 0,
        modalities,
        price_amount: price || null,
        price_currency: currency,
        requires_intake_form: requiresIntake,
        intake_form_template: intakeTemplateId ? parseInt(intakeTemplateId, 10) : null,
        requires_first_visit_in_person: requiresInPerson,
        is_active: isActive,
      };
      return isEdit
        ? practice.procedures.update(procedure!.id, payload)
        : practice.procedures.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Procedure updated.' : 'Procedure created.');
      qc.invalidateQueries({ queryKey: ['practice', 'procedures'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const valid = name.trim().length > 1 && specialtyId && parseInt(duration, 10) > 0
    && modalities.length > 0;

  return (
    <Dialog open onClose={() => !saveMut.isPending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit procedure' : 'New procedure'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={8}>
            <TextField fullWidth required label="Name"
                       value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="GP consultation" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth select required label="Specialty"
                       value={specialtyId}
                       onChange={(e) => setSpecialtyId(e.target.value)}>
              {(specialtiesQ.data ?? []).filter((s) => s.is_active).map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Short description"
                       helperText="Shown in search results."
                       value={shortDesc}
                       onChange={(e) => setShortDesc(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={3} label="Description"
                       value={description}
                       onChange={(e) => setDescription(e.target.value)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField fullWidth required label="Duration (min)" type="number"
                       value={duration}
                       onChange={(e) => setDuration(e.target.value)}
                       inputProps={{ min: 5, max: 240 }} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField fullWidth label="Buffer (min)" type="number"
                       value={buffer}
                       onChange={(e) => setBuffer(e.target.value)}
                       inputProps={{ min: 0, max: 60 }}
                       helperText="Time between bookings." />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField fullWidth label="Price"
                       value={price} onChange={(e) => setPrice(e.target.value)}
                       inputMode="decimal"
                       placeholder="120.00" />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField fullWidth select label="Currency"
                       value={currency}
                       onChange={(e) => setCurrency(e.target.value)}>
              {CURRENCIES.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}>
              Modalities
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {MODALITIES.map((m) => {
                const selected = modalities.includes(m.code);
                return (
                  <Chip
                    key={m.code} label={m.label}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => setModalities(
                      selected
                        ? modalities.filter((x) => x !== m.code)
                        : [...modalities, m.code]
                    )}
                  />
                );
              })}
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={requiresIntake}
                               onChange={(_, v) => setRequiresIntake(v)} />}
              label="Requires intake form" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select label="Intake template"
                       value={intakeTemplateId}
                       onChange={(e) => setIntakeTemplateId(e.target.value)}
                       disabled={!requiresIntake}>
              <MenuItem value="">(none)</MenuItem>
              {(intakeTemplatesQ.data ?? []).map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={requiresInPerson}
                               onChange={(_, v) => setRequiresInPerson(v)} />}
              label="First visit must be in person" />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={isActive}
                               onChange={(_, v) => setIsActive(v)} />}
              label="Active (bookable)" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveMut.isPending}>Cancel</Button>
        <Button variant="contained"
                disabled={!valid || saveMut.isPending}
                onClick={() => saveMut.mutate()}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
